import {
  TMargin,
  backgroundColor,
  backgroundImage,
  borderRadius,
  boxShadow,
  display,
  flexDirection,
  fontSize,
  gap,
  gradientColorStops,
  gridTemplateRows,
  height,
  justifyContent,
  margin,
  maxHeight,
  maxWidth,
  padding,
  position,
  tailwindClassNameConvertNegative,
  textColor,
  twCls,
  width,
} from '@/style';
import { useEffect, useState } from 'react';
import { GhostButton } from '../Button';
import {
  TemperatureAtTarget,
  TemperatureDown,
  TemperatureIdle,
  TemperatureUp,
  TemperatureWarming,
} from '../Icons';
import { VerticalSlider } from '../VerticalSlider';

const mixChannelColors = (colorChannelA: number, colorChannelB: number, amountToMix: number) => {
  const channelA = colorChannelA * amountToMix;
  const channelB = colorChannelB * (1 - amountToMix);
  return Math.round(channelA + channelB);
};

const rgbToHex = (r: number, g: number, b: number) =>
  '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const getLinearGradientPointValues = (
  fromColor: string,
  toColor: string,
  valuePoint: number,
): ColorPair | null => {
  const rgbA = hexToRgb(fromColor);
  const rgbB = hexToRgb(toColor);
  if (!rgbA || !rgbB) {
    return null;
  }

  const mixerFactor = valuePoint;

  const r = mixChannelColors(rgbA.r, rgbB.r, mixerFactor);
  const g = mixChannelColors(rgbA.g, rgbB.g, mixerFactor);
  const b = mixChannelColors(rgbA.b, rgbB.b, mixerFactor);

  return {
    currentColor: `rgb(${r},${g},${b})`,
    lighterColor: getLighterColor(r, g, b),
  } as ColorPair;
};

const getLighterColor = (r: number, g: number, b: number) => {
  const rgbA = [255, 255, 255];
  const rgbB = [r, g, b];
  const mixerFactor = 0.5;
  const lr = mixChannelColors(rgbA[0], rgbB[0], mixerFactor);
  const lg = mixChannelColors(rgbA[1], rgbB[1], mixerFactor);
  const lb = mixChannelColors(rgbA[2], rgbB[2], mixerFactor);
  return `rgb(${lr},${lg},${lb})`;
};

const getThermostatStatusImage = (temperatureValue: number, setPoint: number) => {
  if (temperatureValue === setPoint) {
    return <TemperatureAtTarget />;
  }

  if (temperatureValue > setPoint) {
    return <TemperatureIdle />;
  }

  if (temperatureValue < setPoint) {
    return <TemperatureWarming />;
  }
};

interface ColorPair {
  currentColor: string;
  lighterColor: string;
}

interface ColorPalette {
  [key: number]: ColorPair | null;
}

export const defineColorPalette = (
  fromColor: string,
  toColor: string,
  minValue: number,
  maxValue: number,
  step: number,
): ColorPalette => {
  const palette: ColorPalette = {};
  for (let i = minValue; i <= maxValue; i += step) {
    const colorMixer = ((i - minValue) * (100 / (maxValue - minValue))) / 100;
    palette[i] = getLinearGradientPointValues(fromColor, toColor, colorMixer);
  }

  return palette;
};

export const findClosestNumberInArray = (array: number[], num: number) => {
  let closest = array[0];
  for (const item of array) {
    if (Math.abs(item - num) < Math.abs(closest - num)) {
      closest = item;
    }
  }
  return closest;
};

export interface ThermostatProps {
  temperature: number;
  initialSetPoint: number;
  minValue?: number;
  maxValue?: number;
  sliderStep?: number;
  buttonStep?: number;
  gradientFromColor?: string;
  gradientToColor?: string;
  onSetPointChangeCommitted: (value: number) => void;
  onSetPointIncreased: (value: number) => void;
  onSetPointDecreased: (value: number) => void;
}

export const Thermostat = ({
  temperature,
  initialSetPoint,
  minValue = 5,
  maxValue = 30,
  sliderStep = 0.5,
  buttonStep = 0.5,
  gradientFromColor = '#ff0000',
  gradientToColor = '#44c0fe',
  onSetPointChangeCommitted,
  onSetPointIncreased,
  onSetPointDecreased,
}: ThermostatProps): JSX.Element => {
  const [setPoint, setSetPoint] = useState<number>(0);

  const [colorPalette, setColorPalette] = useState<ColorPalette | null>(null);
  const [sliderColors, setSliderColors] = useState<ColorPair | null>(null);

  const [sliderTrackBorderRadius, setSliderTrackBorderRadius] = useState<string | undefined>(
    undefined,
  );
  const [statusImage, setStatusImage] = useState<JSX.Element | undefined>(undefined);

  const changeSetPoint = (value: number) => {
    setSetPoint(value);
    if (colorPalette) {
      const closestIndex = findClosestNumberInArray(
        Object.keys(colorPalette).map((k) => Number(k)),
        value,
      );
      setSliderColors(colorPalette[closestIndex]);
    }

    let borderRadius: string;
    switch (value) {
      case maxValue:
        borderRadius = '0.5rem 0.5rem 0.5rem 0.5rem';
        break;
      case maxValue - 0.1:
      case maxValue - 0.2:
        borderRadius = '0.4rem 0.4rem 0.5rem 0.5rem';
        break;
      case maxValue - 0.3:
        borderRadius = '0.3rem 0.3rem 0.5rem 0.5rem';
        break;
      case maxValue - 0.4:
        borderRadius = '0.2rem 0.2rem 0.5rem 0.5rem';
        break;
      case maxValue - 0.5:
        borderRadius = '0.1rem 0.1rem 0.5rem 0.5rem';
        break;
      default:
        borderRadius = '0 0 0.5rem 0.5rem';
        break;
    }
    setSliderTrackBorderRadius(borderRadius);
  };

  const onSetPointChange = (_: Event, newValue: number | number[]) => {
    let value = Array.isArray(newValue) ? newValue[0] : newValue;
    if (value < minValue) {
      value = minValue;
    }

    changeSetPoint(value);
  };

  const handleSetPointChangeCommitted = (newValue: number | number[]) => {
    let value = Array.isArray(newValue) ? newValue[0] : newValue;
    if (value < minValue) {
      value = minValue;
    }

    onSetPointChangeCommitted(value);
    setStatusImage(getThermostatStatusImage(temperature, value));
  };

  const handleIncreaseTemperatureSetPoint = () => {
    if (setPoint < maxValue) {
      const newValue = Math.min(maxValue, setPoint + buttonStep);
      changeSetPoint(newValue);
      onSetPointIncreased(newValue);
      setStatusImage(getThermostatStatusImage(temperature, newValue));
    }
  };

  const handleDecreaseTemperatureSetPoint = () => {
    if (setPoint > minValue) {
      const newValue = Math.max(minValue, setPoint - buttonStep);
      changeSetPoint(newValue);
      onSetPointDecreased(newValue);
      setStatusImage(getThermostatStatusImage(temperature, newValue));
    }
  };

  useEffect(() => {
    const colorPalette = defineColorPalette(
      gradientFromColor,
      gradientToColor,
      minValue,
      maxValue,
      (maxValue - minValue) / 10,
    );
    setColorPalette(colorPalette);
  }, [gradientFromColor, gradientToColor, maxValue, minValue, sliderStep]);

  useEffect(() => {
    if (colorPalette) {
      changeSetPoint(initialSetPoint);
      setStatusImage(getThermostatStatusImage(temperature, initialSetPoint));
    }
  }, [colorPalette, initialSetPoint, temperature]);

  return (
    <>
      {setPoint && (
        <div
          className={twCls(
            width('w-4/5'),
            maxWidth('max-w-xs'),
            boxShadow('shadow-shades-inner'),
            backgroundImage('bg-gradient-to-b'),
            gradientColorStops('from-grey-200', 'to-white'),
            padding('p-2'),
            borderRadius('rounded-2xl'),
            display('grid'),
            maxHeight('max-h-112'),
          )}
        >
          <div
            className={twCls(
              width('w-full'),
              height('h-full'),
              backgroundColor('bg-white'),
              boxShadow('shadow-shades-outer'),
              borderRadius('rounded-xl'),
              display('grid'),
            )}
          >
            <div
              className={twCls(
                position('relative'),
                padding('px-5', 'pt-0'),
                display('grid'),
                gridTemplateRows('grid-rows-min-fc-min-2'),
                gap('gap-1'),
              )}
            >
              <div className={twCls(display('flex'), justifyContent('justify-center'))}>
                <GhostButton onClick={handleIncreaseTemperatureSetPoint}>
                  <TemperatureUp />
                </GhostButton>
              </div>
              <div>
                <VerticalSlider
                  sx={{
                    color: 'white',
                    width: '100%',
                    boxSizing: 'border-box',
                    borderRadius: 2,
                    borderWidth: 0,
                    '& .MuiSlider-track': {
                      borderWidth: 0,
                      borderRadius: `${sliderTrackBorderRadius}`,
                      background: `linear-gradient(${sliderColors?.currentColor}, ${sliderColors?.lighterColor})`,
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: sliderColors?.lighterColor,
                    },
                    '& .MuiSlider-thumb': {
                      height: 4,
                      width: 54,
                      marginBottom: -0.8,
                      borderRadius: 4,
                    },
                    '& .MuiSlider-valueLabel': {
                      fontSize: 18,
                      top: setPoint <= maxValue - 2.5 ? 0 : 38,
                      left: 12,
                      backgroundColor: 'unset',
                      color: setPoint <= maxValue - 2.5 ? sliderColors?.currentColor : 'white',
                    },
                  }}
                  valueLabelDisplay="on"
                  valueLabelFormat={(value: number) => `${value} °C`}
                  min={minValue - 1}
                  max={maxValue}
                  step={sliderStep}
                  value={setPoint}
                  onChange={onSetPointChange}
                  onChangeCommitted={(_, val) => handleSetPointChangeCommitted(val)}
                />
              </div>
              <div className={twCls(display('flex'), justifyContent('justify-center'))}>
                <GhostButton onClick={handleDecreaseTemperatureSetPoint}>
                  <TemperatureDown />
                </GhostButton>
              </div>
              <div className={twCls(display('flex'), padding('pb-4'))}>
                <div
                  className={twCls(
                    width('w-1/2'),
                    display('flex'),
                    justifyContent('justify-center'),
                    textColor('text-blue-100'),
                  )}
                >
                  <div className={twCls(fontSize('text-4xl'))}>{temperature}</div>
                  <div className={twCls(display('flex'), flexDirection('flex-col'))}>
                    <div className={twCls(fontSize('text-4xl'))}>°</div>
                    <div
                      className={twCls(
                        fontSize('text-base'),
                        margin('ml-0.5'),
                        tailwindClassNameConvertNegative<TMargin>('-mt-6'),
                      )}
                    >
                      C
                    </div>
                  </div>
                </div>
                <div
                  className={twCls(
                    width('w-1/2'),
                    display('flex'),
                    justifyContent('justify-center'),
                    padding('pl-6'),
                    tailwindClassNameConvertNegative<TMargin>('-mt-0.5'),
                  )}
                >
                  {statusImage}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
